/**
 * Keys, and what they do.
 *
 * Every action worth a key has a name here, a suggested key, and a line saying
 * what it is. What somebody actually binds is kept in the browser, because which
 * key opens the bag is a fact about the person playing rather than about the
 * character.
 *
 * Inventory slots are actions too, bound by middle clicking the slot. They are
 * not listed below because there are twenty eight of them and nobody wants to
 * scroll past twenty eight rows to find the one that opens their skills.
 */

export type KeyAction =
  | 'panel.inventory'
  | 'panel.combat'
  | 'panel.skills'
  | 'panel.quests'
  | 'panel.friends'
  | 'panel.settings'
  | 'panel.equipment'
  | 'panel.spells'
  | 'panel.faith'
  | 'panel.collection'
  | 'panel.boards'
  | 'panel.exchange'
  | 'system.menu'
  | 'system.chat'
  | 'move.run'
  | 'world.map'
  | 'stance.one'
  | 'stance.two'
  | 'stance.three'
  | 'stance.four'
  | 'fight.retaliate'
  | 'ui.adjust';

export interface KeyGroup {
  title: string;
  actions: { action: KeyAction; label: string; about?: string }[];
}

/** Everything that can be bound, in the order the settings show it. */
export const KEY_GROUPS: readonly KeyGroup[] = [
  {
    title: 'Panels',
    actions: [
      { action: 'panel.inventory', label: 'Inventory' },
      { action: 'panel.equipment', label: 'Equipment' },
      { action: 'panel.spells', label: 'Spells' },
      { action: 'panel.faith', label: 'Faith' },
      { action: 'panel.combat', label: 'Fighting' },
      { action: 'panel.skills', label: 'Skills' },
      { action: 'panel.quests', label: 'Quests' },
      { action: 'panel.friends', label: 'Friends' },
      { action: 'panel.settings', label: 'Settings' },
      { action: 'panel.collection', label: 'Collection log' },
      { action: 'panel.boards', label: 'Leaderboards' },
      { action: 'panel.exchange', label: 'Exchange' },
    ],
  },
  {
    title: 'Fighting',
    actions: [
      {
        action: 'stance.one',
        label: 'First stance',
        about: 'Accurate with a blade or a bow, air with a staff.',
      },
      {
        action: 'stance.two',
        label: 'Second stance',
        about: 'Aggressive with a blade, overdrawn with a bow, water with a staff.',
      },
      {
        action: 'stance.three',
        label: 'Third stance',
        about: 'Defensive with a blade, rapid with a bow, earth with a staff.',
      },
      {
        action: 'stance.four',
        label: 'Fourth stance',
        about: 'Controlled with a blade, longrange with a bow, fire with a staff.',
      },
      { action: 'fight.retaliate', label: 'Hit back on your own', about: 'Turns it on and off.' },
    ],
  },
  {
    title: 'Getting about',
    actions: [
      { action: 'move.run', label: 'Run' },
      { action: 'world.map', label: 'The whole island', about: 'The map over the world.' },
      { action: 'system.chat', label: 'Chat' },
      { action: 'system.menu', label: 'The menu', about: 'Escape always does this as well.' },
    ],
  },
  {
    title: 'Interface',
    actions: [
      {
        action: 'ui.adjust',
        label: 'UI adjustment',
        about: 'Hold it to move and resize windows and gauges.',
      },
    ],
  },
];

/**
 * Actions that last as long as a key is held, rather than happening once when it
 * is pressed.
 *
 * Bound to a key on its own, modifiers included: Alt by itself is exactly what
 * somebody holds to rearrange an interface, where for a press it would only be
 * half of a binding.
 */
export const HELD_ACTIONS: ReadonlySet<string> = new Set<KeyAction>(['ui.adjust']);

/** A key on its own, whatever else was held with it: what a held action is bound to. */
export function bareKeyOf(event: KeyboardEvent): string {
  if (event.key === 'Control') return 'Ctrl';
  if (event.key === ' ') return 'Space';
  return event.key.length === 1 ? event.key.toLowerCase() : event.key;
}

/** What each action is bound to before anybody says otherwise. */
export const DEFAULT_KEYS: Readonly<Record<KeyAction, string>> = {
  'panel.combat': 'F1',
  'panel.skills': 'F2',
  'panel.quests': 'F3',
  'panel.inventory': 'F4',
  'panel.equipment': 'F5',
  'panel.spells': 'F8',
  'panel.faith': 'F9',
  'panel.friends': 'F6',
  'panel.settings': 'F7',
  'panel.collection': 'F10',
  'panel.boards': 'F11',
  'panel.exchange': 'F12',
  'system.menu': '',
  'system.chat': 'Enter',
  'move.run': 'r',
  'world.map': 'm',
  'stance.one': '1',
  'stance.two': '2',
  'stance.three': '3',
  'stance.four': '4',
  'fight.retaliate': 'q',
  'ui.adjust': 'Alt',
};

/** What is bound to what, including the slots, which are named `slot.0` upwards. */
export type KeyBindings = Record<string, string>;

const STORAGE_KEY = 'inverse.keys';

export function loadKeys(): KeyBindings {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_KEYS };

    const held = JSON.parse(raw) as Partial<KeyBindings>;
    return { ...DEFAULT_KEYS, ...moved(held) };
  } catch {
    return { ...DEFAULT_KEYS };
  }
}

/**
 * Bindings written before the stances were counted rather than named.
 *
 * The four buttons used to be the four melee stances. They are now whichever
 * four the way somebody is fighting offers, so a key bound to the aggressive
 * stance is a key bound to the second of them.
 */
const RENAMED: Readonly<Record<string, string>> = {
  'stance.accurate': 'stance.one',
  'stance.aggressive': 'stance.two',
  'stance.defensive': 'stance.three',
  'stance.controlled': 'stance.four',
};

function moved(held: Partial<KeyBindings>): Partial<KeyBindings> {
  const bindings: Partial<KeyBindings> = {};

  for (const [action, key] of Object.entries(held)) {
    const named = RENAMED[action] ?? action;
    if (key !== undefined) bindings[named] = key;
  }
  return bindings;
}

export function saveKeys(bindings: KeyBindings): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(bindings));
  } catch {
    // Storage is unavailable inside some embeds. The keys last for this session.
  }
}

/**
 * How a key press is written down.
 *
 * Modifiers are part of it, so Shift and a number is a different binding from the
 * number on its own. Letters are lower cased, because a key is the key rather
 * than what the shift state made of it.
 */
export function keyOf(event: KeyboardEvent): string {
  const parts: string[] = [];
  if (event.ctrlKey) parts.push('Ctrl');
  if (event.altKey) parts.push('Alt');
  if (event.shiftKey) parts.push('Shift');

  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  parts.push(key === ' ' ? 'Space' : key);

  return parts.join('+');
}

/** What a binding looks like where somebody reads it. */
export function readable(binding: string): string {
  if (!binding) return 'not set';
  return binding
    .split('+')
    .map((part) => (part.length === 1 ? part.toUpperCase() : part))
    .join(' + ');
}

/**
 * The same press written down by where the key is rather than what it prints.
 *
 * A binding is recorded as the letter it typed, which is the right thing to show
 * somebody. It is the wrong thing to match on for anybody whose keyboard is not
 * the one the binding was made on: the key under the finger that set `r` prints
 * something else on a French or a Dvorak layout, and the binding silently stops
 * working with nothing to say why.
 *
 * Nothing is written in this form. It is only ever a second thing to try.
 */
export function placeOf(event: KeyboardEvent): string | null {
  const found = /^Key([A-Z])$|^Digit([0-9])$/.exec(event.code);
  if (!found) return null;

  const parts: string[] = [];
  if (event.ctrlKey) parts.push('Ctrl');
  if (event.altKey) parts.push('Alt');
  if (event.shiftKey) parts.push('Shift');
  parts.push((found[1] ?? found[2] ?? '').toLowerCase());

  return parts.join('+');
}

/**
 * Whichever action a key is bound to, or nothing.
 *
 * What it printed first, and where it sits second, so a binding made on one
 * layout still works on another.
 */
export function actionFor(
  bindings: KeyBindings,
  pressed: string,
  place?: string | null,
): string | null {
  for (const [action, binding] of Object.entries(bindings)) {
    if (binding && binding === pressed) return action;
  }

  if (place && place !== pressed) {
    for (const [action, binding] of Object.entries(bindings)) {
      if (binding && binding === place) return action;
    }
  }

  return null;
}

/** The slot a `slot.n` action names, or null for anything else. */
export function slotOf(action: string): number | null {
  const match = /^slot\.(\d+)$/.exec(action);
  return match ? Number(match[1]) : null;
}
